import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleSafetyProtection = () => {
  const BASE = '/images/help/safety-protection';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/sp-step-1.png`, alt: 'VeRO protection overview', caption: 'Step 1 – How VeRO claims affect your eBay listings' },
    ],
    step2: [
      { src: `${BASE}/sp-step-2.png`, alt: 'Blocked Brands management panel', caption: 'Step 2 – Manage blocked brands to prevent VeRO violations' },
    ],
    step3: [
      { src: `${BASE}/sp-step-3.png`, alt: 'Blocked Keywords panel', caption: 'Step 3 – Manage blocked keywords to filter risky listings' },
    ],
    step4: [
      { src: `${BASE}/sp-step-4.png`, alt: 'Whitelist management panel', caption: 'Step 4 – Whitelist authorised brands to override block rules' },
    ],
  };

  return (
    <div>
      <SeoHead
        title="Safety & Protection | CodeRom Help"
        description="How CodeRom protects your eBay account with VeRO checks, stock guards & price limits."
      />
      <HelpBreadcrumbSchema slug="safety-protection" name="Safety & Protection" />
      <Navbar />
      <div className="help-article-page">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">

              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Safety & Protection</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <span className="category" style={{ background: '#fee2e2', color: '#dc2626' }}>🛡️ Safety</span>
                <h1>Safety & Protection</h1>
                <p className="article-subtitle">Protect your eBay store from VeRO violations using blocked brands, keywords, and whitelist rules.</p>
                <div className="article-meta">
                  <span className="read-time">⏱ 8 min read</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h4>On this page</h4>
                <ul>
                  <li><a href="#step1">What is VeRO and why does it matter?</a></li>
                  <li><a href="#step2">Blocked Brands</a></li>
                  <li><a href="#step3">Blocked Keywords</a></li>
                  <li><a href="#step4">Whitelist</a></li>
                  <li><a href="#step5">Auto-Block and Remove Brand from Title</a></li>
                  <li><a href="#step6">Load defaults</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: What is VeRO and why does it matter?</h2>
                  <p>VeRO (Verified Rights Owner) is eBay's programme that allows brand owners and rights holders to report and remove listings that infringe their intellectual property. If you list a product from a brand that has enrolled in VeRO, eBay can remove your listing and — in repeated cases — restrict your selling account.</p>
                  <p>Common VeRO triggers include:</p>
                  <ul>
                    <li>Listing products from brands that have not authorised third-party resellers</li>
                    <li>Using brand names or trademarked terms in your listing title or description</li>
                    <li>Selling replica, counterfeit, or unauthorised items</li>
                  </ul>
                  <div className="tip-box">
                    <span className="tip-icon">🛡️</span>
                    <div className="tip-content">
                      <strong>Coderom's Safety & Protection</strong> tab (Settings → Safety) gives you full control over which brands and keywords to block, so Coderom never imports or lists products that could trigger a VeRO claim.
                    </div>
                  </div>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: What is VeRO?" />
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: Blocked Brands</h2>
                  <p>The Blocked Brands list tells Coderom which brand names to avoid entirely. When a product's title or brand field matches an entry on this list, Coderom will skip it during scraping and never import it to your draft products.</p>
                  <h3>How to manage your Blocked Brands</h3>
                  <ol>
                    <li>Go to <strong>Settings → Safety</strong></li>
                    <li>Find the <strong>Blocked Brands</strong> panel (red header)</li>
                    <li>Type or paste brand names — one per line</li>
                    <li>Use the <strong>Load VeRO Brands</strong> button to instantly load Coderom's curated list of known VeRO-enrolled brands</li>
                    <li>Click <strong>Save</strong> to apply</li>
                  </ol>
                  <div className="tip-box">
                    <span className="tip-icon">💡</span>
                    <div className="tip-content">
                      <strong>Import / Export:</strong> Use the upload and download buttons on the Blocked Brands panel to import a CSV or export your current list for backup.
                    </div>
                  </div>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: Blocked Brands" />
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Blocked Keywords</h2>
                  <p>Blocked Keywords let you filter out products based on words in their title or description — even if the brand itself is not blocked. This is useful for generic terms that frequently appear in problematic listings.</p>
                  <h3>Examples of useful blocked keywords</h3>
                  <ul>
                    <li>Brand-specific model names (e.g. "AirPods", "PlayStation")</li>
                    <li>Terms indicating unauthorised items (e.g. "replica", "inspired by", "compatible with Apple")</li>
                    <li>Categories you don't want to sell (e.g. "adult", "weapon", "knife")</li>
                  </ul>
                  <h3>How to manage Blocked Keywords</h3>
                  <ol>
                    <li>Go to <strong>Settings → Safety</strong></li>
                    <li>Find the <strong>Blocked Keywords</strong> panel (yellow header)</li>
                    <li>Enter keywords — one per line</li>
                    <li>Use <strong>Load Keywords</strong> to load Coderom's recommended keyword list</li>
                    <li>Click <strong>Save</strong></li>
                  </ol>
                  <div className="tip-box">
                    <span className="tip-icon">⚠️</span>
                    <div className="tip-content">
                      <strong>Be specific:</strong> Overly broad keywords (e.g. "pro", "air") may accidentally block legitimate products. Review your list regularly.
                    </div>
                  </div>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Blocked Keywords" />
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Whitelist</h2>
                  <p>The Whitelist overrides the Blocked Brands list. If you are an authorised reseller of a brand that also appears on your blocked list, add it to the Whitelist — Coderom will allow products from that brand even if it matches a blocked entry.</p>
                  <h3>How to manage your Whitelist</h3>
                  <ol>
                    <li>Go to <strong>Settings → Safety</strong></li>
                    <li>Find the <strong>Whitelist</strong> panel (green header)</li>
                    <li>Enter brand names you are authorised to resell — one per line</li>
                    <li>Use <strong>Load Whitelist Defaults</strong> to load Coderom's suggested whitelist</li>
                    <li>Click <strong>Save</strong></li>
                  </ol>
                  <div className="tip-box">
                    <span className="tip-icon">✅</span>
                    <div className="tip-content">
                      <strong>Whitelist takes priority:</strong> A brand on both the blocked list and the whitelist will always be allowed. Use this for brands where you have an official reseller agreement.
                    </div>
                  </div>

                  <HelpScreenshot images={screenshots.step4} stepLabel="Step 4: Whitelist" />
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Auto-Block VeRO Brands and Remove Brand from Title</h2>
                  <p>These two toggles in <strong>Settings → Safety</strong> add an extra layer of automatic protection:</p>

                  <div className="settings-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Auto-Block VeRO Brands</strong></td>
                          <td>Automatically skips products from blocked brands during scraping — no manual review needed</td>
                          <td>✅ On</td>
                        </tr>
                        <tr>
                          <td><strong>Remove Brand from Title</strong></td>
                          <td>Automatically removes the brand name from the beginning of the eBay listing title — reduces VeRO keyword triggers</td>
                          <td>✅ On</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="tip-box">
                    <span className="tip-icon">💡</span>
                    <div className="tip-content">
                      <strong>Auto-Block VeRO Brands</strong> works in real time during scraping. When active, a "Protection Active" badge appears on the toggle. We recommend keeping this on at all times.
                    </div>
                  </div>
                </div>

                {/* Step 6 */}
                <div id="step6" className="content-section">
                  <h2>Step 6: Load defaults</h2>
                  <p>Coderom maintains curated default lists to help you get protected quickly. You can load these with one click from the top of the Safety settings page:</p>

                  <div className="settings-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Button</th>
                          <th>What it loads</th>
                          <th>When to use</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Load VeRO Brands</strong></td>
                          <td>Adds Coderom's curated list of known VeRO-enrolled brands to your Blocked Brands</td>
                          <td>When setting up for the first time or after a reset</td>
                        </tr>
                        <tr>
                          <td><strong>Load Keywords</strong></td>
                          <td>Adds a recommended set of high-risk keywords to your Blocked Keywords list</td>
                          <td>When you want a solid baseline keyword filter</td>
                        </tr>
                        <tr>
                          <td><strong>Load Whitelist Defaults</strong></td>
                          <td>Adds a set of commonly whitelisted brands that are generally safe to resell</td>
                          <td>After loading VeRO Brands, to restore commonly allowed brands</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="tip-box">
                    <span className="tip-icon">🔄</span>
                    <div className="tip-content">
                      <strong>Loading defaults merges — it does not replace.</strong> Existing entries in your lists are preserved. You can always export your current list before loading defaults as a backup.
                    </div>
                  </div>
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <div className="next-steps">
                    <Link to="/help/amazon-filters" className="next-step-card">
                      <span className="next-step-icon">🔍</span>
                      <div className="next-step-content">
                        <h4>Amazon supplier filters</h4>
                        <p>Control which Amazon offers Coderom uses for pricing and stock</p>
                      </div>
                    </Link>
                    <Link to="/help/orders" className="next-step-card">
                      <span className="next-step-icon">📦</span>
                      <div className="next-step-content">
                        <h4>Order management</h4>
                        <p>Track and manage your eBay orders from one place</p>
                      </div>
                    </Link>
                    <Link to="/help/reports" className="next-step-card">
                      <span className="next-step-icon">📊</span>
                      <div className="next-step-content">
                        <h4>Reports & notifications</h4>
                        <p>Monitor your store performance with daily and weekly reports</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was this helpful */}
                <div className="helpful-section">
                  <p className="helpful-text">Was this article helpful?</p>
                  <div className="helpful-buttons">
                    <button className="helpful-btn">👍 Yes</button>
                    <button className="helpful-btn">👎 No</button>
                  </div>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
      <GuestChatWidget />
      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }
        .breadcrumb-section { margin-bottom: 24px; font-size: 14px; color: #64748b; }
        .breadcrumb-link { color: #6366f1; text-decoration: none; }
        .breadcrumb-link:hover { text-decoration: underline; }
        .breadcrumb-separator { margin: 0 8px; }
        .article-header { margin-bottom: 32px; }
        .article-header h1 { font-size: 36px; font-weight: 700; color: #1e293b; margin: 16px 0 12px; }
        .article-subtitle { font-size: 18px; color: #475569; margin-bottom: 16px; }
        .article-meta { display: flex; gap: 16px; }
        .read-time, .category { font-size: 13px; padding: 4px 12px; border-radius: 20px; }
        .read-time { background: #f1f5f9; color: #64748b; }
        .category { font-weight: 600; }
        .table-of-contents { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px 24px; margin-bottom: 40px; }
        .table-of-contents h4 { font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.05em; }
        .table-of-contents ul { list-style: none; padding: 0; margin: 0; }
        .table-of-contents li { margin-bottom: 8px; }
        .table-of-contents a { color: #6366f1; text-decoration: none; font-size: 14px; }
        .table-of-contents a:hover { text-decoration: underline; }
        .content-section { margin-bottom: 48px; }
        .content-section h2 { font-size: 24px; font-weight: 700; color: #1e293b; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0; }
        .content-section h3 { font-size: 18px; font-weight: 600; color: #374151; margin: 24px 0 12px; }
        .content-section p { color: #475569; line-height: 1.7; margin-bottom: 16px; }
        .content-section ul, .content-section ol { color: #475569; padding-left: 24px; margin-bottom: 16px; }
        .content-section li { margin-bottom: 8px; line-height: 1.6; }
        .tip-box { display: flex; gap: 12px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 16px; margin: 20px 0; }
        .tip-icon { font-size: 20px; flex-shrink: 0; }
        .tip-content { font-size: 14px; color: #1e40af; line-height: 1.6; }
        .tip-content strong { color: #1e3a8a; }
        .settings-table { margin: 20px 0; overflow-x: auto; }
        .settings-table table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .settings-table th { background: #f8fafc; padding: 10px 14px; text-align: left; font-weight: 600; color: #374151; border-bottom: 2px solid #e2e8f0; }
        .settings-table td { padding: 10px 14px; border-bottom: 1px solid #f1f5f9; color: #475569; vertical-align: top; }
        .settings-table tbody tr:hover { background: #f8fafc; }
        .whats-next-section { margin-top: 48px; padding-top: 32px; border-top: 2px solid #e2e8f0; }
        .whats-next-section h2 { font-size: 22px; font-weight: 700; color: #1e293b; margin-bottom: 20px; }
        .next-steps { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; }
        .next-step-card { display: flex; gap: 12px; padding: 16px; background: white; border: 1px solid #e2e8f0; border-radius: 10px; text-decoration: none; transition: all 0.2s; }
        .next-step-card:hover { border-color: #6366f1; box-shadow: 0 4px 12px rgba(99,102,241,0.1); }
        .next-step-icon { font-size: 24px; flex-shrink: 0; }
        .next-step-content h4 { font-size: 14px; font-weight: 600; color: #1e293b; margin: 0 0 4px; }
        .next-step-content p { font-size: 13px; color: #64748b; margin: 0; }
        .helpful-section { text-align: center; padding: 32px; background: #f8fafc; border-radius: 12px; margin-top: 32px; }
        .helpful-text { font-size: 16px; color: #374151; margin-bottom: 16px; }
        .helpful-buttons { display: flex; gap: 12px; justify-content: center; }
        .helpful-btn { padding: 8px 24px; border: 1px solid #e2e8f0; border-radius: 8px; background: white; cursor: pointer; font-size: 14px; transition: all 0.2s; }
        .helpful-btn:hover { border-color: #6366f1; color: #6366f1; }
        @media (max-width: 768px) {
          .help-article-page { padding: 80px 0 60px; }
          .article-header h1 { font-size: 28px; }
          .next-steps { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default HelpArticleSafetyProtection;
